import { calculateDuration } from "../utils";
import { isDefined } from "@clipboard-health/util-ts";
import meta from "src/utils/meta";
import { getBreakDuration } from "@src/appV2/Shifts/utils/getBreakDuration";
import { formatTime } from "../utils";

const { maxShiftDuration, minShiftDuration } = meta();

// Constants for validation
export const LUNCH_BREAK_THRESHOLD_HOURS = 6;

export interface ValidationError {
  shiftType: string;
  error: string;
}

export interface ShiftWarning {
  shiftType: string;
  warning: string;
}

export interface OverlapWarning {
  shiftType1: string;
  shiftType2: string;
  warning: string;
}

/**
 * Validates if shift duration is within acceptable bounds
 */
export function validateShiftDuration(
  startTime: Date | undefined,
  endTime: Date | undefined
): string | null {
  if (!startTime || !endTime) {
    return null;
  }

  // Check if start time is before end time
  if (startTime >= endTime) {
    return "Start time must be before end time";
  }

  const totalShiftHours = calculateDuration(startTime, endTime);
  if (!isDefined(totalShiftHours)) {
    return "Invalid time range";
  }

  const durationInHours = totalShiftHours.hours + totalShiftHours.minutes / 60;

  if (durationInHours > maxShiftDuration) {
    return `Shift duration cannot exceed ${maxShiftDuration} hours`;
  }

  if (durationInHours < minShiftDuration) {
    return `Shift duration must be at least ${minShiftDuration} hours`;
  }

  return null;
}

/**
 * Validates if shift duration is within acceptable bounds (returns boolean for schema validation)
 * This function is designed to be used with Zod schema validation
 */
export function isShiftDurationValid(startTime: Date, endTime: Date): boolean {
  const totalShiftHours = calculateDuration(startTime, endTime);
  if (!isDefined(totalShiftHours)) {
    return false;
  }

  const durationInHours = totalShiftHours.hours + totalShiftHours.minutes / 60;
  return durationInHours >= minShiftDuration && durationInHours <= maxShiftDuration;
}

/**
 * Checks if a shift duration exceeds the lunch break threshold
 */
export function requiresLunchBreak(
  startTime: Date | undefined,
  endTime: Date | undefined
): boolean {
  if (!startTime || !endTime) {
    return false;
  }

  const totalShiftHours = calculateDuration(startTime, endTime);
  if (!isDefined(totalShiftHours)) {
    return false;
  }

  const durationInHours = totalShiftHours.hours + totalShiftHours.minutes / 60;
  return durationInHours > LUNCH_BREAK_THRESHOLD_HOURS;
}

/**
 * Gets warnings for shifts that require lunch breaks
 */
export function getShiftWarnings(
  shifts: Array<{ startTime?: Date; endTime?: Date; shiftType: string }>
): ShiftWarning[] {
  const warnings: ShiftWarning[] = [];

  shifts.forEach(({ startTime, endTime, shiftType }) => {
    if (requiresLunchBreak(startTime, endTime)) {
      warnings.push({
        shiftType,
        warning: `Shift is longer than ${LUNCH_BREAK_THRESHOLD_HOURS} hours and will include a mandatory lunch break`,
      });
    }
  });

  return warnings;
}

/**
 * Checks for overlapping shifts between different shift types
 */
export function getOverlapWarnings(
  shifts: Array<{ startTime?: Date; endTime?: Date; shiftType: string }>
): OverlapWarning[] {
  const warnings: OverlapWarning[] = [];

  // Check for overlaps between consecutive shifts
  for (let i = 0; i < shifts.length - 1; i++) {
    const current = shifts[i];
    const next = shifts[i + 1];

    if (current.startTime && current.endTime && next.startTime && next.endTime) {
      // Check if shifts overlap (start1 < end2 && start2 < end1)
      if (current.startTime < next.endTime && next.startTime < current.endTime) {
        warnings.push({
          shiftType1: current.shiftType,
          shiftType2: next.shiftType,
          warning: `${current.shiftType} and ${next.shiftType} shifts overlap`,
        });
      }
    }
  }

  // Special case: NOC vs AM (next day) - NOC shifts typically end early morning
  const nocShift = shifts.find((s) => s.shiftType === "NOC");
  const amShift = shifts.find((s) => s.shiftType === "AM");

  if (nocShift?.startTime && nocShift?.endTime && amShift?.startTime && amShift?.endTime) {
    // For NOC vs AM overlap, we need to handle shifts that span across midnight
    // NOC shift typically ends early morning (12 AM - 8 AM)
    // AM shift typically starts morning (6 AM - 12 PM)

    // Normalize NOC end time to the same day as AM start time for comparison
    const normalizedNocEnd = new Date(amShift.startTime);
    normalizedNocEnd.setHours(nocShift.endTime.getHours(), nocShift.endTime.getMinutes());

    // Only warn if there's an actual overlap (normalized NOC end is after AM start)
    if (normalizedNocEnd > amShift.startTime) {
      warnings.push({
        shiftType1: "NOC",
        shiftType2: "AM",
        warning: "NOC and AM shifts overlap (NOC shift extends into AM shift time)",
      });
    }
  }

  return warnings;
}

/**
 * Validates multiple shifts and returns all validation errors
 */
export function validateMultipleShifts(
  shifts: Array<{ startTime?: Date; endTime?: Date; shiftType: string }>
): ValidationError[] {
  return shifts
    .map(({ startTime, endTime, shiftType }) => ({
      shiftType,
      error: validateShiftDuration(startTime, endTime),
    }))
    .filter(({ error }) => error !== null) as ValidationError[];
}

/**
 * Gets the total duration of a shift in hours
 */
export function getShiftDurationInHours(
  startTime: Date | undefined,
  endTime: Date | undefined
): number {
  if (!startTime || !endTime) {
    return 0;
  }

  const totalShiftHours = calculateDuration(startTime, endTime);
  if (!isDefined(totalShiftHours)) {
    return 0;
  }

  return totalShiftHours.hours + totalShiftHours.minutes / 60;
}

/**
 * Format duration with break information
 * This function consolidates the formatDurationWithBreak logic from utils.ts
 */
export function formatDurationWithBreak(
  startTime: Date,
  endTime: Date,
  mandatoryBreakConfiguration?: {
    breakDurationInMinutes: number;
  }
): string {
  const totalDuration = calculateDuration(startTime, endTime);
  if (!totalDuration) {
    return "Invalid time range";
  }

  const includesLunchBreak = requiresLunchBreak(startTime, endTime);
  if (!includesLunchBreak) {
    return formatTime(totalDuration);
  }

  const breakDurationInMinutes = getBreakDuration(mandatoryBreakConfiguration);
  const breakDurationTimeObject = {
    hours: Math.floor(breakDurationInMinutes / 60),
    minutes: breakDurationInMinutes % 60,
  };

  const workingDurationInMinutes =
    totalDuration.hours * 60 + totalDuration.minutes - breakDurationInMinutes;
  const workingDurationTimeObject = {
    hours: Math.floor(workingDurationInMinutes / 60),
    minutes: workingDurationInMinutes % 60,
  };

  return `${formatTime(workingDurationTimeObject)} + ${formatTime(breakDurationTimeObject)} break`;
}
